/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useRef, useEffect } from 'react';
import { FiUser, FiShoppingCart, FiSearch, FiLogOut, FiPackage, FiX } from 'react-icons/fi';

const Header = ({
  theme,
  isSticky,
  logoName,
  storeData,
  cartCount,
  onCartClick,
  onUserClick,
  searchQuery,
  onSearchChange,
  customer,
  onLogout,
  onOrdersClick
}) => {
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowAccountMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <div className="h-[64px] sm:h-[77px]" />
      {isSticky && <div className="h-[52px] sm:h-[49px]" />}

      {/* Main Nav */}
      <nav className="fixed top-0 left-0 right-0 bg-white px-4 sm:px-8 py-3 flex items-center justify-between shadow-sm z-[60]">
        {/* Left: Icons */}
        <div className="flex items-center gap-3 text-gray-500 text-xl">
          {customer ? (
            <div className="relative" ref={menuRef}>
              <div
                className="relative cursor-pointer hover:text-gray-800 transition-colors"
                onClick={() => setShowAccountMenu(v => !v)}
              >
                <FiUser />
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full border border-white"></div>
              </div>

              {showAccountMenu && (
                <div
                  className="absolute right-0 top-full mt-3 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[140px] z-[70]"
                  style={{ animation: 'fadeInDown 0.2s ease-out' }}
                >
                  <div className="px-3 py-2 border-b border-gray-100 text-right">
                    <p className="font-bold text-gray-800 text-sm">{customer.name || customer.customerName}</p>
                    {customer.phone && <p className="text-[11px] text-gray-400 mt-0.5" dir="ltr">{customer.phone}</p>}
                  </div>

                  <button
                    onClick={() => {
                      if (onOrdersClick) onOrdersClick();
                      setShowAccountMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FiPackage className="text-gray-400" />
                    <span className="font-medium">طلباتي</span>
                  </button>

                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <button
                      onClick={() => { onLogout(); setShowAccountMenu(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <FiLogOut />
                      <span className="font-medium">تسجيل الخروج</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <FiUser className="cursor-pointer hover:text-gray-800 transition-colors" onClick={onUserClick} />
          )}

          {/* Cart */}
          <div className="relative cursor-pointer hover:text-gray-800 transition-colors" onClick={onCartClick}>
            <FiShoppingCart />
            {cartCount > 0 && (
              <span className={`absolute -top-1 -right-1 ${theme.bg} text-white text-[9px] sm:text-[10px] w-3 sm:w-4 h-3 sm:h-4 rounded-full flex items-center justify-center font-bold`}>
                {cartCount}
              </span>
            )}
          </div>
        </div>

        {/* Center: Search */}
        <div className="flex-1 max-w-full sm:max-w-lg mx-2 sm:mx-8 relative">
          <input
            type="text"
            placeholder="ابحث عن منتج..."
            className="w-full bg-gray-50 border border-gray-200 rounded-lg py-2 px-10 text-sm focus:outline-none focus:ring-1 focus:ring-offset-0"
            style={{ '--tw-ring-color': theme.primary }}
            value={searchQuery}
            onChange={onSearchChange}
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange({ target: { value: '' } })}
              className="absolute left-3 sm:left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiX className="text-sm" />
            </button>
          )}
        </div>

        {/* Right: Logo */}
        <div className="max-w-[140px] sm:max-w-[260px] text-right leading-tight">
          <div className={`text-xl sm:text-2xl font-bold truncate ${theme.text}`}>
            {storeData.storeName}
          </div>
          <p className="hidden sm:block text-[11px] text-gray-500 truncate mt-0.5">
            {storeData.description || storeData.storeSlogan || storeData.sectorType}
          </p>
        </div>
      </nav>

      {/* Sticky Secondary Nav */}
      <div className={`fixed top-[64px] sm:top-[77px] left-0 w-full bg-white border-b border-gray-100 z-[55] transition-all duration-300 ${isSticky ? 'translate-y-0 opacity-100 shadow-sm' : '-translate-y-full opacity-0 pointer-events-none'}`}>
        <div className="container mx-auto px-4 sm:px-8 py-2 flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0">
          <div className="flex gap-4 sm:gap-8 text-sm font-bold text-gray-600 flex-wrap justify-center sm:justify-start">
            <span
              className="cursor-pointer hover:text-[color:var(--tw-ring-color)] font-bold"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              الرئيسية
            </span>
            <span
              className="cursor-pointer hover:text-[color:var(--tw-ring-color)]"
              onClick={() => document.getElementById('categories-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              كل الأقسام
            </span>
            <span
              className="cursor-pointer hover:text-[color:var(--tw-ring-color)]"
              onClick={() => document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              العروض
            </span>
          </div>
          <button
            className={`${theme.bg} text-white px-4 sm:px-6 py-1.5 rounded-lg text-sm font-bold`}
            onClick={onCartClick}
          >
            تسوق سريع
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default Header;
